import React, {PropsWithChildren, useContext, useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {SortByEnum, XpertKnowledgeAssetsDto, XpertKnowledgeDto, XpertKnowledgeStatus} from '../../services/XpertKnowledge/type'
import {XpertKnowledgeService} from '../../services/XpertKnowledge/XpertKnowledge'
import {XpertKnowledgeGeneratorService} from '../../services/XpertKnowledgeGenerator'
import {XpertKnowledgeBufferService} from '../../services/XpertKnowledgeBuffer'
import {getCookie, getCookieName} from '../../common/utils/cookieUtils'
import {UserDto} from '../../services/User/type'
import {UserService} from '../../services/User/User'
import {XpertKnowledgePublicTutorialService} from '../../services/XpertKnowledgePublicTutorial'
import {XpertKnowledgeTutorialGenerationType, XpertKnowledgeTutorialStatus} from '../../services/XpertKnowledgeTutorial/type'
import {WebSocketService} from '../../services/WebSocketService'
import {useXpertKnowledgeAuthContext} from '../../components/AuthContext'
import XpertKnowledgeTutorial from '../XpertKnowledgeTutorial'

interface FilterState {
  userId?: number
  equipmentType?: string
  startTimestamp?: string
  endTimestamp?: string
  sortBy: SortByEnum
  title?: string
}

interface XpertKnowledgeContext {
  xpertKnowledgesPublish: XpertKnowledgeDto[]
  xpertKnowledgesUnpublish: XpertKnowledgeDto[]
  xpertKnowledgesBuffer: XpertKnowledgeDto[]
  selectedXpertKnowledge?: XpertKnowledgeDto
  activeFilters: FilterState
  setActiveFilters: (filters: FilterState) => void
  fetchXpertKnowledges: (
    userId?: number,
    equipmentType?: string,
    startTimestamp?: string,
    endTimestamp?: string,
    sortBy?: SortByEnum,
    isNoLoader?: boolean,
    title?: string
  ) => Promise<void>
  fetchNextPage: () => Promise<void>
  hasMorePages: boolean
  totalPublish: number
  isLoadingNextPage: boolean
  fetchXpertKnowledgeById: (id: string) => Promise<XpertKnowledgeDto>
  fetchXpertKnowledgeAssets: (xpertKnowledgeId: string) => Promise<XpertKnowledgeAssetsDto>
  setSelectedXpertKnowledge: (xk?: XpertKnowledgeDto) => void
  setXpertKnowledgesUnpublish: (xks: XpertKnowledgeDto[]) => void
  deleteXpertKnowledge: (xpertKnowledgeId: string) => Promise<void>
  hideXpertKnowledgeBuffer: (xpertKnowledgeRef: string) => Promise<void>
  regenerateXpertKnowledge: (xk: XpertKnowledgeDto, type: XpertKnowledgeTutorialGenerationType) => Promise<void>
  makeXpertKnowledgeTutorialPublic: (xpertKnowledgeId: string) => Promise<void>
  makeXpertKnowledgeTutorialPrivate: (xpertKnowledgeId: string) => Promise<void>
  isLoadingPublish: boolean
  isLoadingUnpublish: boolean
  creators: UserDto[]
  equipments: string[]
  wsConnectionStatus: 'connected' | 'disconnected' | 'connecting'
}

const Context = React.createContext<XpertKnowledgeContext>(null as any)

interface XpertKnowledgeContextProviderProps {}

export const XpertKnowledgeContextProvider: React.FC<PropsWithChildren<XpertKnowledgeContextProviderProps>> = ({children}) => {
  const [xpertKnowledgesPublish, setXpertKnowledgesPublish] = useState<XpertKnowledgeDto[]>([])
  const [creators, setCreators] = useState<UserDto[]>([])
  const [equipments, setEquipments] = useState<string[]>([])
  const [xpertKnowledgesUnpublish, setXpertKnowledgesUnpublish] = useState<XpertKnowledgeDto[]>([])
  const [xpertKnowledgesBuffer, setXpertKnowledgesBuffer] = useState<XpertKnowledgeDto[]>([])
  const [selectedXpertKnowledge, setSelectedXpertKnowledge] = useState<XpertKnowledgeDto>()
  const [isLoadingPublish, setIsLoadingPublish] = useState<boolean>(false)
  const [isLoadingUnpublish, setIsLoadingUnpublish] = useState<boolean>(false)
  const [isLoadingNextPage, setIsLoadingNextPage] = useState<boolean>(false)
  const [wsConnectionStatus, setWsConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting'>('disconnected')
  const [currentUser, setCurrentUser] = useState<UserDto | null>(null)

  const [activeFilters, setActiveFilters] = useState<FilterState>({
    userId: undefined,
    equipmentType: undefined,
    startTimestamp: undefined,
    endTimestamp: undefined,
    sortBy: SortByEnum.DEFAULT,
    title: undefined,
  })

  const [currentPage, setCurrentPage] = useState<number>(1)
  const [hasMorePages, setHasMorePages] = useState<boolean>(true)
  const [totalPublish, setTotalPublish] = useState<number>(0)
  const {user} = useXpertKnowledgeAuthContext()
  const [currentFilters, setCurrentFilters] = useState<{
    userId?: number
    equipmentType?: string
    startTimestamp?: string
    endTimestamp?: string
    sortBy?: SortByEnum
  }>({})

  const connectWebSocket = async () => {
    if (currentUser?.activationId) {
      setWsConnectionStatus('connecting')
      try {
        WebSocketService.addConnectionListener(handleConnectionStatus)
        WebSocketService.registerConversationHandler('xpert_knowledge_updates', handleWebSocketMessage)
        await WebSocketService.connect()
      } catch (error) {
        console.error('Failed to connect to WebSocket:', error)
        setWsConnectionStatus('disconnected')
      }
    }
  }

  const handleConnectionStatus = (status: 'connected' | 'disconnected') => {
    setWsConnectionStatus(status)

    if (status === 'connected' && currentUser?.activationId) {
      WebSocketService.registerForActivationUpdates(currentUser.activationId.toString())
    }
  }

  const handleWebSocketMessage = (data: any) => {
    switch (data.type) {
      case 'xpert_knowledge_generation_started':
        handleGenerationStarted(data)
        break
      case 'xpert_knowledge_tutorial_processed':
        handleTutorialProcessed(data)
        break
      case 'xpert_knowledge_generation_completed':
        handleGenerationCompleted(data)
        break
      case 'xpert_knowledge_generation_failed':
        handleGenerationFailed(data)
        break
      case 'xpert_knowledge_generation_error':
        handleGenerationError(data)
        break
      case 'xpert_knowledge_audio_generated':
        handleAudioGenerated(data)
        break
      default:
        console.log('Unhandled WebSocket message type:', data.type)
    }
  }

  useEffect(() => {
    const getCurrentUser = async () => {
      const authToken = getCookie(getCookieName())
      if (authToken) {
        try {
          const user = await UserService.getMe()
          setCurrentUser(user)
        } catch (error) {
          console.error('Error fetching current user:', error)
        }
      }
    }

    if (location.pathname.includes('/xpert-knowledge/sso') || location.pathname.includes('/rag/sso')) return
    getCurrentUser()
  }, [user])

  useEffect(() => {
    if (currentUser?.activationId) {
      if (WebSocketService.isConnected()) {
        setWsConnectionStatus('connected')

        WebSocketService.addConnectionListener(handleConnectionStatus)
        WebSocketService.registerConversationHandler('xpert_knowledge_updates', handleWebSocketMessage)
        WebSocketService.registerForActivationUpdates(currentUser.activationId.toString())
      } else {
        connectWebSocket()
      }
    }

    return () => {
      console.log('Cleaning up WebSocket listeners')
      WebSocketService.removeConnectionListener(handleConnectionStatus)
      WebSocketService.unregisterConversationHandler('xpert_knowledge_updates')
    }
  }, [currentUser?.activationId])

  const handleGenerationStarted = (data: any) => {
    const xpertKnowledgeId = data.xpert_knowledge_id
    console.log(`Generation started for XK: ${xpertKnowledgeId}`)

    updateXpertKnowledgeStatus(xpertKnowledgeId, XpertKnowledgeStatus.IN_GENERATION)
  }

  const handleTutorialProcessed = (data: any) => {
    const xpertKnowledgeId = data.xpert_knowledge_id
    console.log(`Tutorial processed for XK: ${xpertKnowledgeId}`)
  }

  const handleGenerationCompleted = async (data: any) => {
    const xpertKnowledgeId = data.xpert_knowledge_id
    console.log(`Generation completed for XK: ${xpertKnowledgeId}`)

    try {
      updateXpertKnowledgeStatus(xpertKnowledgeId, XpertKnowledgeStatus.DONE)
      const updatedXk = await fetchXpertKnowledgeById(xpertKnowledgeId)

      setXpertKnowledgesPublish((prev) => prev.map((xk) => (xk.id === xpertKnowledgeId ? updatedXk : xk)))
      setXpertKnowledgesUnpublish((prev) => prev.map((xk) => (xk.id === xpertKnowledgeId ? updatedXk : xk)))

      if (selectedXpertKnowledge?.id === xpertKnowledgeId) {
        setSelectedXpertKnowledge(updatedXk)
      }
    } catch (error) {
      console.error('Error updating XK after generation completion:', error)
    }
  }

  const handleGenerationFailed = (data: any) => {
    const xpertKnowledgeId = data.xpert_knowledge_id
    const reason = data.data?.reason || 'Unknown error'
    console.log(`Generation failed for XK: ${xpertKnowledgeId}, reason: ${reason}`)

    const failedStatus = reason === 'EMPTY_TRANSCRIPT' ? XpertKnowledgeStatus.FAILED_EMPTY_TRANSCRIPT : XpertKnowledgeStatus.FAILED_GENERATION
    updateXpertKnowledgeStatus(xpertKnowledgeId, failedStatus)
  }

  const handleGenerationError = (data: any) => {
    const xpertKnowledgeId = data.xpert_knowledge_id
    const error = data.data?.error || 'Unknown error'
    console.log(`Generation error for XK: ${xpertKnowledgeId}, error: ${error}`)

    updateXpertKnowledgeStatus(xpertKnowledgeId, XpertKnowledgeStatus.FAILED_GENERATION)
  }

  const handleAudioGenerated = (data: any) => {
    const xpertKnowledgeId = data.xpert_knowledge_id
    console.log(`Audio generated for XK: ${xpertKnowledgeId}`)
  }

  const updateXpertKnowledgeStatus = (xpertKnowledgeId: string, status: XpertKnowledgeStatus) => {
    setXpertKnowledgesPublish((prev) => prev.map((xk) => (xk.id === xpertKnowledgeId ? {...xk, status} : xk)))
    setXpertKnowledgesUnpublish((prev) => prev.map((xk) => (xk.id === xpertKnowledgeId ? {...xk, status} : xk)))

    if (selectedXpertKnowledge?.id === xpertKnowledgeId) {
      setSelectedXpertKnowledge((prev) => (prev ? {...prev, status} : prev))
    }
  }

  const deleteXpertKnowledge = async (xpertKnowledgeId: string) => {
    await XpertKnowledgeService.destroy({xpertKnowledgeId})
    setXpertKnowledgesPublish((prev) => prev.filter((xk) => xk.id !== xpertKnowledgeId))
    setXpertKnowledgesUnpublish((prev) => prev.filter((xk) => xk.id !== xpertKnowledgeId))
  }

  const hideXpertKnowledgeBuffer = async (xpertKnowledgeRef: string) => {
    await XpertKnowledgeBufferService.hideBuffer({xpertKnowledgeRef})
    setXpertKnowledgesBuffer((prev) => prev.filter((xk) => xk.reference !== xpertKnowledgeRef))
  }

  const makeXpertKnowledgeTutorialPublic = async (xpertKnowledgeId: string) => {
    const xk = await XpertKnowledgePublicTutorialService.makeTutorialPublic(xpertKnowledgeId)
    setSelectedXpertKnowledge(xk)
  }

  const makeXpertKnowledgeTutorialPrivate = async (xpertKnowledgeId: string) => {
    const xk = await XpertKnowledgePublicTutorialService.makeTutorialPrivate(xpertKnowledgeId)
    setSelectedXpertKnowledge(xk)
  }

  const regenerateXpertKnowledge = async (xpertKnowledge: XpertKnowledgeDto, type: XpertKnowledgeTutorialGenerationType) => {
    try {
      await XpertKnowledgeGeneratorService.regenerate(xpertKnowledge.id, type)

      setXpertKnowledgesPublish((prev) => prev.map((xk) => (xk.id === xpertKnowledge.id ? {...xk, status: XpertKnowledgeStatus.IN_GENERATION} : xk)))
      setXpertKnowledgesUnpublish((prev) =>
        prev.map((xk) => (xk.id === xpertKnowledge.id ? {...xk, status: XpertKnowledgeStatus.IN_GENERATION} : xk))
      )
    } catch (error) {
      console.error('Error regenerating XpertKnowledges', error)
    }
  }

  const fetchXpertKnowledges = async (
    userId?: number,
    equipmentType?: string,
    startTimestamp?: string,
    endTimestamp?: string,
    sortBy?: SortByEnum,
    isNoLoader?: boolean,
    title?: string
  ) => {
    if (!isNoLoader) setIsLoadingPublish(true)

    setCurrentPage(1)
    setHasMorePages(true)
    setCurrentFilters({userId, equipmentType, startTimestamp, endTimestamp, sortBy})

    try {
      const xpertKnowledgesPublish = await XpertKnowledgeService.getAllPublish(
        userId,
        equipmentType,
        startTimestamp,
        endTimestamp,
        sortBy,
        1, // page
        20, // size
        title
      )

      xpertKnowledgesPublish.items = xpertKnowledgesPublish.items.filter(
        (xk) => xk.publishTutorial !== undefined && xk.publishTutorial?.status !== XpertKnowledgeTutorialStatus.DEPRECATED
      )

      setXpertKnowledgesPublish(xpertKnowledgesPublish.items)
      setHasMorePages(xpertKnowledgesPublish.page < xpertKnowledgesPublish.pages)
      setTotalPublish(xpertKnowledgesPublish.total)
    } catch (error) {
      console.error('Error fetching XpertKnowledges:', error)
    } finally {
      setIsLoadingPublish(false)
    }
  }

  const fetchNextPage = async () => {
    if (!hasMorePages || isLoadingNextPage) return

    setIsLoadingNextPage(true)
    const nextPage = currentPage + 1

    try {
      const response = await XpertKnowledgeService.getAllPublish(
        currentFilters.userId,
        currentFilters.equipmentType,
        currentFilters.startTimestamp,
        currentFilters.endTimestamp,
        currentFilters.sortBy,
        nextPage,
        20
      )

      setXpertKnowledgesPublish((prev) => [...prev, ...response.items])
      setCurrentPage(nextPage)
      setHasMorePages(nextPage < response.pages)
    } catch (error) {
      console.error('Error fetching next page:', error)
    } finally {
      setIsLoadingNextPage(false)
    }
  }

  const fetchXpertKnowledgeCreators = async (): Promise<UserDto[]> => {
    try {
      const users = await XpertKnowledgeService.getAllCreator()
      setCreators(users)
      return users
    } catch (error) {
      console.error('Error fetching Xpert Knowledge Assets:', error)
      throw error
    }
  }

  const fetchXpertKnowledgeEquipments = async (): Promise<string[]> => {
    try {
      const equipments = await XpertKnowledgeService.getAllEquipmentTypes()
      setEquipments(equipments)
      return equipments
    } catch (error) {
      console.error('Error fetching Xpert Knowledge Assets:', error)
      throw error
    }
  }

  const fetchXpertKnowledgeAssets = async (xpertKnowledgeId: string): Promise<XpertKnowledgeAssetsDto> => {
    try {
      const assets = await XpertKnowledgeService.getAssets(xpertKnowledgeId)
      if (!assets) {
        throw new Error('Failed to fetch Xpert Knowledge Assets')
      }
      return assets
    } catch (error) {
      console.error('Error fetching Xpert Knowledge Assets:', error)
      throw error
    }
  }

  const fetchXpertKnowledgeById = async (id: string) => {
    const xpertKnowledge = await XpertKnowledgeService.getById(id)
    return xpertKnowledge
  }

  useEffect(() => {
    setIsLoadingPublish(true)
    const loadXpertKnowledges = async (isNoLoader?: boolean) => {
      const authToken = getCookie(getCookieName())
      if (authToken) {
        setIsLoadingUnpublish(true)
        const xpertKnowledgesUnpublish = await XpertKnowledgeService.getAllUnpublish(1, 100)
        xpertKnowledgesUnpublish.items = xpertKnowledgesUnpublish.items.filter(
          (xk) => xk.publishTutorial !== undefined && xk.publishTutorial?.status !== XpertKnowledgeTutorialStatus.DEPRECATED
        )
        setTotalPublish
        setXpertKnowledgesUnpublish(xpertKnowledgesUnpublish.items)

        const xpertKnowledgesBuffer = await XpertKnowledgeBufferService.getAll()
        setXpertKnowledgesBuffer(xpertKnowledgesBuffer.items)
        setIsLoadingUnpublish(false)
        await fetchXpertKnowledgeCreators()
        await fetchXpertKnowledgeEquipments()
        await fetchXpertKnowledges(undefined, undefined, undefined, undefined, undefined, isNoLoader)
      }
    }
    if (location.pathname.includes('/xpert-knowledge/sso') || location.pathname.includes('/rag/sso')) return
    loadXpertKnowledges()
  }, [getCookie(getCookieName())])

  useEffect(() => {
    return () => {
      WebSocketService.disconnect()
    }
  }, [])

  return (
    <Context.Provider
      value={{
        xpertKnowledgesPublish,
        xpertKnowledgesUnpublish,
        xpertKnowledgesBuffer,
        selectedXpertKnowledge,
        activeFilters,
        setActiveFilters,
        fetchXpertKnowledges,
        fetchNextPage,
        hasMorePages,
        isLoadingNextPage,
        totalPublish,
        fetchXpertKnowledgeById,
        fetchXpertKnowledgeAssets,
        setSelectedXpertKnowledge,
        setXpertKnowledgesUnpublish,
        deleteXpertKnowledge,
        hideXpertKnowledgeBuffer,
        regenerateXpertKnowledge,
        makeXpertKnowledgeTutorialPublic,
        makeXpertKnowledgeTutorialPrivate,
        isLoadingPublish,
        isLoadingUnpublish,
        creators,
        equipments,
        wsConnectionStatus,
      }}
    >
      {children}
    </Context.Provider>
  )
}

export const useXpertKnowledgeContext = () => useContext(Context)
